import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_SPEECH_LANGUAGES = {
    en: 'en-US',
    es: 'es-MX',
};

const getRecognitionConstructor = () =>
    window.SpeechRecognition || window.webkitSpeechRecognition;

const joinTranscript = (prefix, transcript) =>
    [prefix.trim(), transcript.trim()].filter(Boolean).join(' ');

const getSpeechLanguage = (language) => {
    const normalizedLanguage = language?.replace('_', '-') || '';
    const baseLanguage = normalizedLanguage.split('-')[0].toLowerCase();

    return normalizedLanguage.includes('-')
        ? normalizedLanguage
        : DEFAULT_SPEECH_LANGUAGES[baseLanguage] || normalizedLanguage;
};

const useSpeechRecognition = ({ language, onTranscript }) => {
    const recognitionRef = useRef(null);
    const initialTextRef = useRef('');
    const onTranscriptRef = useRef(onTranscript);
    const acceptsResultsRef = useRef(false);
    const [isListening, setIsListening] = useState(false);
    const [error, setError] = useState('');
    const [isSupported, setIsSupported] = useState(false);

    onTranscriptRef.current = onTranscript;

    useEffect(() => {
        const Recognition = getRecognitionConstructor();
        if (!Recognition) return undefined;

        const recognition = new Recognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = getSpeechLanguage(language);
        recognition.onstart = () => {
            acceptsResultsRef.current = true;
            setIsListening(true);
        };
        recognition.onend = () => {
            acceptsResultsRef.current = false;
            setIsListening(false);
        };
        recognition.onerror = ({ error: recognitionError }) => {
            acceptsResultsRef.current = false;
            setIsListening(false);
            if (recognitionError !== 'aborted') setError(recognitionError);
        };
        recognition.onresult = ({ results }) => {
            if (!acceptsResultsRef.current) return;
            const transcript = Array.from(results)
                .map((result) => result[0]?.transcript || '')
                .join(' ');
            onTranscriptRef.current(
                joinTranscript(initialTextRef.current, transcript)
            );
        };

        recognitionRef.current = recognition;
        setIsSupported(true);

        return () => {
            const shouldAbort = acceptsResultsRef.current;
            acceptsResultsRef.current = false;
            recognition.onstart = null;
            recognition.onend = null;
            recognition.onerror = null;
            recognition.onresult = null;
            if (shouldAbort) recognition.abort();
            recognitionRef.current = null;
        };
    }, [language]);

    const startListening = useCallback((initialText = '') => {
        const recognition = recognitionRef.current;
        if (!recognition) return;

        initialTextRef.current = initialText;
        acceptsResultsRef.current = true;
        setError('');
        try {
            recognition.start();
        } catch (recognitionError) {
            if (recognitionError.name !== 'InvalidStateError') {
                acceptsResultsRef.current = false;
                setError('start-failed');
            }
        }
    }, []);

    const stopListening = useCallback(() => {
        if (!recognitionRef.current || !acceptsResultsRef.current) return;
        acceptsResultsRef.current = false;
        recognitionRef.current.stop();
    }, []);

    const toggleListening = useCallback(
        (initialText = '') => {
            if (isListening) stopListening();
            else startListening(initialText);
        },
        [isListening, startListening, stopListening]
    );

    return {
        error,
        isListening,
        isSupported,
        stopListening,
        toggleListening,
    };
};

export default useSpeechRecognition;
